import { Credentials } from '@quadminds/client-apis/auth';
import { FirebaseError } from 'firebase/app';
import { FormikProps } from 'formik';
import { TFunction } from 'i18next';
import React from 'react';
import { NavigateFunction } from 'react-router-dom';

import { AuthApiError } from '../../services/auth/auth-api-error';
import {
    ErrorPathType,
    FIREBASE_AUTH_ERRORS,
    handleAuthApiError,
    handleFirebaseError,
    LegacyLoginError,
    SaasLegacyLoginErrorCodes,
} from '../../utils';

export type LoginErrorDeps = {
    navigate: NavigateFunction;
    setFirebaseError: (message: string) => void;
    setLegacyLoginError: (code: string) => void;
    t: TFunction;
};

// Firebase codes meaning the user dismissed the popup or the browser cancelled it — no action needed
const DISMISSED_POPUP_CODES: string[] = [
    FIREBASE_AUTH_ERRORS.POPUP_CLOSED_BY_USER,
    FIREBASE_AUTH_ERRORS.CANCELLED_POPUP_REQUEST,
];

// Firebase codes shown inline instead of redirecting to an error page
const INLINE_ERROR_MESSAGE_KEYS: Record<string, string> = {
    [FIREBASE_AUTH_ERRORS.POPUP_BLOCKED]: 'login.error.popupBlocked',
    [FIREBASE_AUTH_ERRORS.ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL]: 'login.error.accountExistsWithDifferentCredential',
};

export const resolveErrorPath = (error: unknown): ErrorPathType | undefined => {
    if (error instanceof FirebaseError) return handleFirebaseError(error);
    if (error instanceof AuthApiError) return handleAuthApiError(error);
    return undefined;
};

const navigateToErrorPath = (errorPath: ErrorPathType, navigate: NavigateFunction) => {
    const [path, state] = errorPath;
    navigate(path, state);
};

const handleLegacyLoginError = (
    error: LegacyLoginError,
    { navigate, setFirebaseError, setLegacyLoginError, t }: LoginErrorDeps,
) => {
    if (error.code === SaasLegacyLoginErrorCodes.BLOCKED) {
        setLegacyLoginError(SaasLegacyLoginErrorCodes.BLOCKED);
        return;
    }
    if (error.code === SaasLegacyLoginErrorCodes.INVALID_CREDENTIALS) {
        setFirebaseError(t('login.invalidUserOrPassword'));
        return;
    }
    navigate('/error');
};

// Handles errors of the email/password login submit
export const handleLoginSubmitError = (error: unknown, deps: LoginErrorDeps) => {
    const { navigate, setFirebaseError, t } = deps;

    if (error instanceof FirebaseError && error.code === 'auth/wrong-password') {
        setFirebaseError(t('login.invalidUserOrPassword'));
        return;
    }

    if (error instanceof LegacyLoginError) {
        handleLegacyLoginError(error, deps);
        return;
    }

    const errorPath = resolveErrorPath(error);
    if (errorPath) {
        navigateToErrorPath(errorPath, navigate);
    } else {
        navigate('/error');
    }
};

// Handles errors of the identity provider (google/microsoft/apple) login
export const handleThirdPartyLoginError = (error: unknown, { navigate, setFirebaseError, t }: LoginErrorDeps) => {
    if (error instanceof FirebaseError) {
        if (DISMISSED_POPUP_CODES.includes(error.code)) return;

        const messageKey = INLINE_ERROR_MESSAGE_KEYS[error.code];
        if (messageKey) {
            setFirebaseError(t(messageKey));
            return;
        }
    }

    const errorPath = resolveErrorPath(error);
    if (errorPath !== undefined) {
        navigateToErrorPath(errorPath, navigate);
        return;
    }
    setFirebaseError(t('login.error.genericError.body'));
};

export const runSafely = async (action: () => Promise<unknown>, onError: (error: unknown) => void) => {
    try {
        await action();
    } catch (error) {
        onError(error);
    }
};

// Browsers do not fire change events on autofill, so values are picked up from the animation hook.
// The field is resolved through data-formik-field because the name attributes are decoys that keep
// the browser from silently reusing stored credentials.
export const applyAutofill = (event: React.AnimationEvent<HTMLInputElement>, formik: FormikProps<Credentials>) => {
    const target = event.target as HTMLInputElement;
    const fieldName = target.dataset['formikField'];
    if (fieldName === 'email' || fieldName === 'password') {
        formik.setFieldValue(fieldName, target.value);
    }
};
